import { Person } from '.';

export type FieldDependency = {
  targetField: string;
  targetIsInArrayField?: boolean;
  visibilityFunction: string;
};

export type FormSpecField = {
  name: string;
  label?: string | React.ReactNode;
  description?: string;
  component: { type: string; params?: any };
  required?: string | { value: boolean; message: string };
  validate?: string[];
  pattern?: string;
  children?: FormSpecField[];
  // TODO: remove field_dependencies
  field_dependencies?: {
    target_field: string;
    target_is_in_array_field?: boolean;
    visibility_function: string;
  }[];
  field_rules?: {
    target_field: string;
    target_is_in_array_field?: boolean;
    condition: RuleCondition;
  }[];
  value_as?: string;
};

export type FormSpecSection = {
  label?: string;
  description?: string;
  form_fields: FormSpecField[];
};

export type FormSpec = {
  id?: string;
  title: string;
  sections: FormSpecSection[];
  default_values?: any;
};

export type StateSetupProps = {
  peopleData: Person[];
  onSubmitSuccess?: () => void;
  onEnterLater?: () => void;
};

export enum PeopleOptionsType {
  businessOwners = 'business_owners',
  officers = 'officers',
  all = 'all',
}

export enum PersonIdentifierType {
  doesNotHave = 'does_not_have',
  ssn = 'ssn',
  itin = 'itin',
}

export interface AddressIFormValues {
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country?: string;
  is_business_address?: boolean;
  is_employee_address?: boolean;
}

export type PersonIFormValues = {
  person_type: string | null;

  // Person fields
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  email: string | null;
  phone_number: string | null;
  identifier_type?: PersonIdentifierType | null;
  identifier?: string | null;
  start_date?: string | null;
  personal_address?: AddressIFormValues | null;
  is_officer?: boolean;
  is_owner?: boolean;
  is_member?: boolean;
  is_llc_member?: boolean;
  birth_date?: string;
  ownership_date?: string | null;
  percent_ownership?: number | null;
};

export type BusinessIFormValues = {
  person_type: string | null;
  name?: string | null;
  business_identifier?: string | null;
  incorporation_region?: string | null;
  incorporation_date?: string | null;
  physical_address?: AddressIFormValues | null;
  mailing_address?: AddressIFormValues | null;
  is_physical_address_same_as_mailing_address?: boolean | null;
  personal_address?: AddressIFormValues | null;
  ownership_date?: string | null;
  percent_ownership?: number | null;
  is_llc_managed?: boolean;
};

export type PersonOrBusinessIFormValues = {
  person_type: string | null;

  // Person fields
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  identifier_type: PersonIdentifierType | null;
  identifier?: string | null;
  start_date?: string | null;
  percent_profit_distribution?: number;
  personal_address?: AddressIFormValues | null;
  is_officer?: boolean;
  is_owner?: boolean;
  birth_date?: string;

  // Business fields
  business_name?: string | null;
  business_identifier?: string | null;
  incorporation_region?: string | null;
  incorporation_date?: string | null;
  physical_address?: AddressIFormValues | null;
  mailing_address?: AddressIFormValues | null;
  is_physical_address_same_as_mailing_address?: boolean | null;

  // Common fields
  ownership_date?: string | null;
  percent_ownership?: number | null;
};

export type SubmitterIFormValues = {
  first_name: string;
  last_name: string;
  title: string;
  email: string;
  phone: string;
  personal_address: AddressIFormValues;
  is_authorized: boolean;
};

export interface OnboardingIFormValues {
  incorporation_state: string;
  entity_type: string;
  tax_class: string;
  is_non_stock?: boolean;
  nonprofit_type?: string;
  llc_managed_type?: string;
  business_name: string;
  business_purpose: string;
  incorporated_date: string;
  naics_code: string;
  accounting: { method: string; period: string; year_end: string };
  mailing_address: { is_business_address: boolean } | AddressIFormValues;
  business_address: AddressIFormValues;
  business_phone: string;
  business_email: string;
  futa_liability_year: string;
  ein: string;
  persons: PersonOrBusinessIFormValues[];
  submitter_contact: SubmitterIFormValues;
}

export interface EmployeeIFormValues {
  first_name: string | null;
  last_name: string | null;
  ssn?: string | null;
  physical_address?: AddressIFormValues | null;
}

export interface CoIFormValues {
  dba_name: string;
  naics_code: string;
  business_activities_description: string;
  physical_address: AddressIFormValues;
  employees: EmployeeIFormValues[];
  filing_frequency: string;
  first_payroll: string;
  no_related_businesses: boolean;
}

export interface GaIFormValues {
  dba_name: string;
  activity_start_date: string;
  first_payroll_date: string;
  employees: {
    num_employed: string;
    all_over_18: boolean;
  };
  num_ga_locations: string;
  fut_filing_year: string;
  business_category: string;
  physical_address: AddressIFormValues;
  estimated_quarterly_wages: number;
}

export interface HiIFormValues {
  dba_name: string;
  business_activities_description: string;
  activity_start_date: string;
  num_hi_locations: string;
  physical_address: AddressIFormValues;
  employees: EmployeeIFormValues[];
  representative: { ref_email: string; start_date: Date };
  contact: {
    ref_email: string;
  };
}

export interface PaIFormValues {
  dba_name: string;
  activity_start_date: string;
  first_payroll_date: string;
  num_pa_locations: number;
  physical_address?: AddressIFormValues;
  est_quarter_gross_wages: string;
  employees: EmployeeIFormValues[];
}

export type SelectOptionType = { name?: string; value: string | number };

export enum RuleConditionType {
  Equals = 'equals',
  Exists = 'exists',
  Not = 'not',
  And = 'and',
  Or = 'or',
  LowerBounds = 'lower_bounds',
  UpperBounds = 'upper_bounds',
}

export type RuleConditionEqual = {
  type: string; // RuleConditionType.Equals;
  value: string | number;
};

export type RuleConditionExists = {
  type: string; // RuleConditionType.Exists;
};

export type RuleConditionNot = {
  type: string; // RuleConditionType.Not;
  not_condition: RuleCondition;
};

export type RuleConditionAnd = {
  type: string; // RuleConditionType.And;
  and_conditions: RuleCondition[];
};

export type RuleConditionOr = {
  type: string; // RuleConditionType.Or;
  or_conditions: RuleCondition[];
};

export type RuleConditionLowerBounds = {
  type: string; // RuleConditionType.LowerBounds;
  value: number;
};

export type RuleConditionUpperBounds = {
  type: string; // RuleConditionType.UpperBounds;
  value: number;
};

export type RuleCondition =
  | RuleConditionEqual
  | RuleConditionNot
  | RuleConditionAnd
  | RuleConditionOr
  | RuleConditionLowerBounds
  | RuleConditionUpperBounds
  | RuleConditionExists;

export type FieldRule = {
  targetField: string;
  targetIsInArrayField?: boolean;
  condition: RuleCondition;
};

export const SsnPattern = {
  value: /^((?!000|666)[0-8][0-9]{2}-(?!00)[0-9]{2}-(?!0000)[0-9]{4})?$/g,
  message: 'The SSN must be valid xxx-xx-xxxx',
};

export const IdentifierPattern = {
  value: /^((?!000|666)[0-8][0-9]{2}-(?!00)[0-9]{2}-(?!0000)[0-9]{4})?$/g,
  message: 'The SSN or ITIN must be valid xxx-xx-xxxx',
};

export const PhonePattern = {
  value: /^\d{3}-\d{3}-\d{4}$/g,
  message: 'The phone number must be valid xxx-xxx-xxxx',
};

export const AnyPhonePattern = {
  value: /([0-9\s-]{7,})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/g,
  message: 'The phone number must be valid',
};

export const EmailPattern = {
  value: /^(.+)@(\S+)[.](\S+)$/g,
  message: 'The email must be valid',
};

export const EinPattern = {
  value: /^(0[1-6]|1[0-6]|2[0-7]|[345]\d|[68][0-8]|7[1-7]|9[0-58-9])-?\d{7}$/g,
  message: 'Your EIN must be 9 digits long',
};

export const NaicsPattern = {
  value: /^\d{6}$/g,
  message: 'Must be 6 digits',
};

export const NumberPattern = {
  value: /^\d+$/g,
  message: 'Must be a number',
};

export const CharLimit100Pattern = {
  value: /^[\w\W\d]{0,100}$/g,
  message: 'Must not exceed 100 characters',
};
